import React from 'react';
import { Text } from 'atoms';
import { size, weight } from 'designs';
import { LegalDoc } from 'data/legal';

type LegalDocumentProps = {
  doc: LegalDoc;
};

/**
 * Renders the `**bold**` runs in a plain-string body. Bodies that need more
 * than emphasis — a link, say — are authored as JSX in `data/legal` and pass
 * through untouched.
 */
const renderBody = (body: React.ReactNode): React.ReactNode => {
  if (typeof body !== 'string') {
    return body;
  }

  return body
    .split(/\*\*(.+?)\*\*/g)
    .map((part, index) =>
      index % 2 === 1 ? <strong key={index}>{part}</strong> : part
    );
};

const LegalDocument: React.FC<LegalDocumentProps> = ({ doc }) => {
  return (
    <article className="legal-document">
      <header className="legal-document-head">
        <Text
          as="h1"
          className="legal-document-title"
          size={size.display}
          weight={weight.bold}
        >
          {doc.title}
        </Text>

        {doc.updated && (
          <Text
            as="p"
            className="legal-document-updated"
            size={size.main}
            weight={weight.semibold}
          >
            Last updated: {doc.updated}
          </Text>
        )}
      </header>

      {doc.sections.map((section, index) => (
        <section className="legal-section" key={index}>
          <Text
            as="h2"
            className="legal-section-header"
            size={size.large}
            weight={weight.bold}
          >
            {section.header}
          </Text>

          {section.body !== undefined && (
            <Text as="p" className="legal-section-body" size={size.medium}>
              {renderBody(section.body)}
            </Text>
          )}

          {section.subsections?.map((subsection, subIndex) => (
            <div className="legal-subsection" key={subIndex}>
              <Text
                as="h3"
                className="legal-subsection-header"
                size={size.medium}
                weight={weight.bold}
              >
                {subsection.header}
              </Text>
              <Text as="p" className="legal-section-body" size={size.medium}>
                {renderBody(subsection.body)}
              </Text>
            </div>
          ))}
        </section>
      ))}
    </article>
  );
};

export default LegalDocument;
